import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Trash2, Phone, Mail, RefreshCw, Loader2, Lock } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nb_admin_token";

const STATUS = {
  nouveau: { label: "Nouveau", cls: "border-terra/30 bg-terra/15 text-terra" },
  en_cours: { label: "En cours", cls: "border-sky-500/30 bg-sky-500/15 text-sky-400" },
  traite: { label: "Traité", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" },
};
const ROOF_LABELS = { tuile: "Tuile canal / romane", ardoise: "Ardoise", zinc: "Zinc", plat: "Toit plat / terrasse" };
const PROJECT_LABELS = { renovation: "Rénovation complète", reparation: "Réparation / fuite", isolation: "Isolation & étanchéité", neuf: "Toiture neuve" };
const URGENCY_LABELS = { standard: "Standard", rapide: "Sous 7 jours", urgence: "Urgence 48 h" };

const inputCls =
  "w-full rounded-md border border-white/10 bg-[#0D0F12] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:border-terra focus:outline-none";

function formatError(e, fallback) {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return fallback;
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);

  const load = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/devis`, { headers: { Authorization: `Bearer ${t}` } });
        setItems(data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setItems(null);
        } else {
          toast.error("Erreur de chargement des demandes");
          setItems([]);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Connexion réussie", { description: "Bienvenue dans votre espace pro." });
    } catch (e2) {
      setError(formatError(e2, "Échec de connexion"));
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/devis/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Statut mis à jour");
      load(token);
    } catch (e) {
      toast.error(formatError(e, "Mise à jour impossible"));
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/devis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Demande supprimée");
      load(token);
    } catch (e) {
      toast.error(formatError(e, "Suppression impossible"));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setItems(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D0F12] px-6">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-white/10 bg-[#14171D] p-8"
        >
          <div className="flex flex-col items-center">
            <Logo size={56} />
            <h1 className="mt-5 font-serif text-2xl text-white">Espace pro</h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Suivi des demandes de devis</p>
          </div>
          <div className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Adresse email"
              className={inputCls}
              required
            />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className={inputCls}
              required
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-terra py-3.5 text-sm font-semibold text-white transition-colors hover:bg-terra-hover disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Se connecter
          </button>
          <a href="/" className="mt-5 block text-center text-xs text-slate-500 transition-colors hover:text-terra">
            ← Retour au site
          </a>
        </motion.form>
      </div>
    );
  }

  const counts = {
    total: items?.length || 0,
    nouveau: items?.filter((d) => d.status === "nouveau").length || 0,
    en_cours: items?.filter((d) => d.status === "en_cours").length || 0,
    traite: items?.filter((d) => d.status === "traite").length || 0,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0D0F12] text-slate-200">
      <header className="border-b border-white/5 bg-[#0A0C0F]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-4">
            <Logo size={40} />
            <div>
              <h1 className="font-serif text-xl text-white">Suivi des devis</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">NB Couverture — Espace pro</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-terra/50 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Actualiser
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            ["total", "Total"],
            ["nouveau", "Nouveaux"],
            ["en_cours", "En cours"],
            ["traite", "Traités"],
          ].map(([k, label]) => (
            <div key={k} data-testid={`admin-stat-${k}`} className="border border-white/5 bg-[#14171D] p-5">
              <p className="font-serif text-3xl text-white">{counts[k]}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 space-y-4">
          {items === null ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-terra" />
            </div>
          ) : items.length === 0 ? (
            <p data-testid="admin-empty" className="border border-white/5 bg-[#14171D] p-12 text-center text-sm text-slate-400">
              Aucune demande de devis pour le moment.
            </p>
          ) : (
            items.map((d) => (
              <motion.article
                key={d.id}
                data-testid={`admin-devis-card-${d.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="border border-white/5 bg-[#14171D] p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-serif text-lg text-white">{d.name}</h2>
                      <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${STATUS[d.status]?.cls || STATUS.nouveau.cls}`}>
                        {STATUS[d.status]?.label || d.status}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {Object.entries(STATUS).map(([key, s]) => (
                      <button
                        key={key}
                        data-testid={`admin-status-${d.id}-${key}`}
                        onClick={() => setStatus(d.id, key)}
                        className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                          d.status === key ? s.cls : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-300"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                    <button
                      data-testid={`admin-delete-${d.id}`}
                      onClick={() => remove(d.id)}
                      aria-label="Supprimer"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-slate-500 transition-colors hover:border-red-500/50 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 border-t border-white/5 pt-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs text-slate-500">Projet</p>
                    <p className="mt-0.5 text-slate-200">{PROJECT_LABELS[d.project_type] || d.project_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Toiture</p>
                    <p className="mt-0.5 text-slate-200">
                      {ROOF_LABELS[d.roof_type] || d.roof_type} · {d.surface} m²
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Délai</p>
                    <p className="mt-0.5 text-slate-200">{URGENCY_LABELS[d.urgency] || d.urgency}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Estimation affichée</p>
                    <p className="mt-0.5 text-slate-200">{d.estimate || "—"}</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  <a href={`tel:${d.phone}`} data-testid={`admin-call-${d.id}`} className="flex items-center gap-2 text-terra transition-colors hover:text-terra-hover">
                    <Phone className="h-3.5 w-3.5" />
                    {d.phone}
                  </a>
                  <a href={`mailto:${d.email}`} className="flex items-center gap-2 text-slate-300 transition-colors hover:text-white">
                    <Mail className="h-3.5 w-3.5" />
                    {d.email}
                  </a>
                </div>

                {d.message && <p className="mt-4 border-l-2 border-terra/40 pl-4 text-sm italic leading-relaxed text-slate-400">« {d.message} »</p>}
              </motion.article>
            ))
          )}
        </div>

        <div className="mt-12 border border-white/5 bg-[#14171D] p-6">
          <h2 className="font-serif text-lg text-white">Kit logo</h2>
          <p className="mt-1 text-xs text-slate-500">Votre blason NB Couverture, prêt pour vos devis, factures et documents.</p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 border border-white/10 bg-[#0D0F12] p-4">
              <img src="/logo.svg" alt="Logo sombre" className="h-14 w-14" />
              <a
                href="/logo.svg"
                download="nb-couverture-logo.svg"
                data-testid="admin-logo-download-dark"
                className="rounded-full border border-terra/40 px-4 py-2 text-xs font-semibold text-terra transition-colors hover:bg-terra hover:text-white"
              >
                Version sombre
              </a>
            </div>
            <div className="flex items-center gap-3 border border-white/10 bg-white p-4">
              <img src="/logo-light.svg" alt="Logo clair" className="h-14 w-14" />
              <a
                href="/logo-light.svg"
                download="nb-couverture-logo-clair.svg"
                data-testid="admin-logo-download-light"
                className="rounded-full border border-terra/40 px-4 py-2 text-xs font-semibold text-terra transition-colors hover:bg-terra hover:text-white"
              >
                Version claire
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
